import React from 'react';
import { Circle, X, Map } from 'lucide-react';
import { cn } from 'lib/utils';
import { usePortalWalkthrough } from './PortalWalkthroughContext';

export default function WalkthroughChecklist() {
  const { isActive, isAtCheckpoint, isPageReady, sections, completedIds, currentSectionId, goToSection, endTour, isFirstTime } =
    usePortalWalkthrough();

  // Only visible between sections (checkpoint mode) — hidden while a section tour is running or page data is loading
  if (!isActive || !isAtCheckpoint || !isPageReady) return null;

  // Exclude the "checklist" intro step from the visible list
  const visibleSections = sections.filter(s => s.id !== 'checklist');
  const completedCount = visibleSections.filter(s =>
    completedIds.includes(s.id)
  ).length;

  return (
    <div
      data-tour="checklist-panel"
      className="fixed right-4 top-1/2 -translate-y-1/2 z-100001 w-56 bg-white rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.2)] overflow-hidden select-none"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3.5 py-2.5 bg-primary">
        <div className="flex items-center gap-2">
          <Map size={15} className="text-white shrink-0" />
          <span className="font-semibold text-sm text-white leading-none">
            Portal Walkthrough
          </span>
        </div>
        {!isFirstTime && (
          <button
            type="button"
            onClick={endTour}
            className="text-white/70 hover:text-white transition-colors bg-transparent border-none cursor-pointer p-0 flex items-center"
            title="Exit tour"
          >
            <X size={15} />
          </button>
        )}
      </div>

      {/* Section list */}
      <div className="py-0 px-0">
        {visibleSections.map(section => {
          const isCompleted = completedIds.includes(section.id);
          const isCurrent = section.id === currentSectionId;
          const isClickable = !isFirstTime;

          return (
            <button
              key={section.id}
              type="button"
              onClick={() => isClickable && goToSection(section.id)}
              disabled={!isClickable}
              className={cn(
                'flex items-center gap-2 w-full px-3 py-1.5 text-left transition-colors text-[13px] bg-white border-0 border-b border-solid border-gray-200',
                isCurrent && 'bg-primary-light! text-primary-darkened font-medium',
                isCompleted && !isCurrent && 'text-[#555]',
                !isCompleted && !isCurrent && 'text-grey-light',
                isClickable && !isCurrent && 'hover:bg-gray-50! cursor-pointer',
                !isClickable && 'cursor-default'
              )}
            >
              {isCompleted ? (
                <Circle
                  size={14}
                  className="text-green-500 shrink-0"
                  fill="currentColor"
                />
              ) : (
                <Circle
                  size={14}
                  className={cn(
                    'shrink-0',
                    isCurrent ? 'text-primary' : 'text-[#ccc]'
                  )}
                />
              )}
              <span className="leading-tight">{section.label}</span>
            </button>
          );
        })}
      </div>

      {/* Footer progress */}
      <div className="px-3.5 py-2 border-t border-[#f0f0f0]">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[11px] text-grey-light">
            {completedCount}/{visibleSections.length} completed
          </span>
          <span className="text-[11px] text-grey-light">
            {Math.round((completedCount / visibleSections.length) * 100)}%
          </span>
        </div>
        <div className="w-full h-1 bg-[#f0f0f0] rounded-full overflow-hidden">
          <div
            className="h-full bg-green-500 rounded-full transition-all duration-500"
            style={{
              width: `${(completedCount / visibleSections.length) * 100}%`,
            }}
          />
        </div>
      </div>
    </div>
  );
}
